/** WebSocket event types sent between client and server */

/**
 * Synthetic model ID for the Cascade Agent. Not a real Claude model — when set
 * as `claude_session.model`, classifier-routing fully owns per-turn model choice
 * (Haiku for cheap_task, Sonnet for dev_work, Opus for deep_reasoning).
 * Source of truth for both backend and frontend.
 */
export const CASCADE_MODEL_ID = 'cascade';

export enum WsEvent {
  /** Client → Server: Capability handshake initialization */
  INITIALIZE = 'INITIALIZE',
  /** Server → Client: Request user approval for a tool execution */
  TOOL_APPROVAL_REQUEST = 'TOOL_APPROVAL_REQUEST',
  /** Client → Server: User approval or denial for a tool execution */
  TOOL_APPROVAL_RESPONSE = 'TOOL_APPROVAL_RESPONSE',
  /** Client → Server: raw audio chunk (binary) */
  AUDIO_CHUNK = 'audio_chunk',
  /** Client → Server: end of PTT recording */
  AUDIO_END = 'audio_end',
  /** Client → Server: discard all buffered audio chunks (sent before combined-blob fallback) */
  AUDIO_RESET = 'audio_reset',
  /** Server → Client: transcribed text from Whisper */
  TRANSCRIPTION = 'transcription',
  /** Server → Client: streaming Claude response chunk */
  CLAUDE_CHUNK = 'claude_chunk',
  /** Server → Client: Claude finished responding */
  CLAUDE_DONE = 'claude_done',
  /** Client → Server: request summary of text for TTS */
  SUMMARIZE = 'summarize',
  /** Server → Client: summarized text ready for TTS */
  SUMMARY_READY = 'summary_ready',
  /** Client → Server: request chat history for current session */
  LOAD_MESSAGES = 'load_messages',
  /** Client → Server: request chat history for a specific session (by ID, no side effects) */
  LOAD_TAB_MESSAGES = 'load_tab_messages',
  /** Server → Client: chat history loaded from DB (initial page) */
  MESSAGES_LOADED = 'messages_loaded',
  /** Client → Server: request older messages (cursor-based pagination) */
  LOAD_OLDER_MESSAGES = 'load_older_messages',
  /** Server → Client: older messages page loaded */
  OLDER_MESSAGES_LOADED = 'older_messages_loaded',
  /** Client → Server: send text + image attachments (no audio) */
  SEND_WITH_ATTACHMENTS = 'send_with_attachments',
  /** Server → Client: error */
  ERROR = 'error',
  /** Client → Server: request user's session list */
  LIST_SESSIONS = 'list_sessions',
  /** Server → Client: user's session list */
  SESSIONS_LIST = 'sessions_list',
  /** Client → Server: switch to a different session */
  SWITCH_SESSION = 'switch_session',
  /** Server → Client: session switch confirmed with messages */
  SESSION_SWITCHED = 'session_switched',
  /** Client → Server: rename a session */
  RENAME_SESSION = 'rename_session',
  /** Server → Client: session renamed */
  SESSION_RENAMED = 'session_renamed',
  /** Client → Server: delete a session */
  DELETE_SESSION = 'delete_session',
  /** Server → Client: session deleted */
  SESSION_DELETED = 'session_deleted',
  /** Server → Client: SDK session expired, started fresh */
  SESSION_RESUME_FAILED = 'session_resume_failed',
  /** Client → Server: send text prompt (no audio) */
  SEND_TEXT = 'send_text',
  /** Server → Client: tool activity from Claude Agent SDK */
  TOOL_ACTIVITY = 'tool_activity',
  /** Server → Client: projects whose context was loaded */
  CONTEXT_LOADED = 'context_loaded',
  /** Client → Server: cancel the running prompt */
  CANCEL_PROMPT = 'cancel_prompt',
  /** Server → Client: prompt was cancelled */
  PROMPT_CANCELLED = 'prompt_cancelled',
  /** Server → Client: context was compacted (older messages summarized) */
  CONTEXT_COMPACTED = 'context_compacted',
  /** Server → Client: Claude is asking the user a question (with interactive chip options) */
  AGENT_QUESTION = 'agent_question',
  /**
   * Client → Server: operator's answer to a prior AGENT_QUESTION.
   * The backend turns it into a prose user turn — `Answering your question
   * "<q>": <answer>. Please proceed using that choice.` — and resumes the
   * session via query({ resume }). A discrete request/response, NOT a
   * tool_result on a held SDK call (the headless SDK already ended the turn).
   */
  AGENT_QUESTION_ANSWER = 'agent_question_answer',

  // SSH remote session events
  /** Client → Server: open SSH connection to a target */
  SSH_CONNECT = 'ssh_connect',
  /** Server → Client: SSH connection established */
  SSH_CONNECTED = 'ssh_connected',
  /** Client → Server: send input to SSH PTY */
  SSH_INPUT = 'ssh_input',
  /** Server → Client: output from SSH PTY */
  SSH_OUTPUT = 'ssh_output',
  /** Client → Server: disconnect SSH session */
  SSH_DISCONNECT = 'ssh_disconnect',
  /** Server → Client: SSH connection closed */
  SSH_DISCONNECTED = 'ssh_disconnected',
  /** Client → Server: resize SSH PTY */
  SSH_RESIZE = 'ssh_resize',
  /** Server → Client: available SSH targets */
  SSH_TARGETS = 'ssh_targets',
  /** Server → Client: authoritative terminal state on (re)connect handshake.
   *  The single truth signal a `detached` client reconciles against — never a
   *  frontend timeout (Frontend FSM pattern rule 3). attached:true carries the
   *  server PTY buffer for replay; attached:false means no live PTY → idle. */
  SSH_STATE = 'ssh_state',

  /** Client → Server: request session health info */
  SESSION_HEALTH = 'session_health',
  /** Server → Client: session health metadata */
  SESSION_HEALTH_DATA = 'session_health_data',

  /** Client → Server: pause/resume auto docs-update for this session */
  TOGGLE_AUTO_DOCS = 'toggle_auto_docs',

  /** Server → Client: a query is still running from a previous connection */
  QUERY_IN_PROGRESS = 'query_in_progress',

  /**
   * Server → Client: authoritative list of session IDs that currently have a running query.
   * Emitted on every connection (fresh + Socket.IO state recovery). Frontend uses this to
   * clear `busy` state for any tab whose session is NOT in the list — fixes the stuck-spinner
   * bug when CLAUDE_DONE is lost to a dead socket on reconnect (issue #1161).
   * Phase 2 (composer-state-machine): payload extended with `lastReceivedClientMsgIds` so
   * the frontend can reconcile its outbound queue (submitting → awaiting_stream | send_failed).
   */
  SESSIONS_RECONCILE = 'sessions_reconcile',

  /**
   * Server → Client: the server FREED a session's busy-slot WITHOUT a terminal frame
   * (CLAUDE_DONE / ERROR / PROMPT_CANCELLED) — so no page will ever be told, by a turn, that it
   * ended. Payload: `SessionIdlePayload { sessionId, reason }`. Emitted to the owner's user room
   * from every non-terminal release site (a bare intake claim released or expired, a silent
   * zombie evicted) — NEVER from a pipeline's own completion (that already emitted its terminal).
   * A page that armed its composer busy from a server snapshot (SESSIONS_RECONCILE /
   * MESSAGES_LOADED.isSessionBusy / QUERY_IN_PROGRESS → composerStore.markServerBusy) reconciles
   * it away on this event: the server-authoritative sync for a claim that never became a turn
   * (voice-assistant#2174, defect 2 — "still busy 100 s later").
   */
  SESSION_IDLE = 'session_idle',

  /**
   * Server → Client: server received and persisted a user message.
   * Emitted immediately after the user message row is saved to DB.
   * Payload: { clientMsgId, serverMsgId, sessionId } — links the frontend's UUID
   * to the DB row id so the composerStore can transition submitting → awaiting_stream.
   */
  MESSAGE_RECEIVED = 'message_received',

  /** Server → Client: prompt accepted mid-turn and queued for after the current turn */
  PROMPT_QUEUED = 'PROMPT_QUEUED',

  /** Server → Client: deploy/rebuild status notification */
  DEPLOY_STATUS = 'deploy_status',

  /** Client → Server: request replay of missed events from Redis Stream */
  REPLAY_EVENTS = 'replay_events',
  /** Server → Client: replayed events from Redis Stream */
  EVENTS_REPLAYED = 'events_replayed',
  /** Client → Server: mid-stream gap recovery — re-send CLAUDE_CHUNK events from a seq gap.
   *  The client detects `seq > expectedSeq` and asks for the missing chunks; the backend
   *  re-reads the user's Redis stream from the last in-order event id and re-emits the
   *  CLAUDE_CHUNK events with `seq >= expectedSeq`. composerStore's seq-buffer reorders/dedupes. */
  REQUEST_MISSING_CHUNKS = 'request_missing_chunks',

  // Voice Memo events
  /** Client → Server: raw audio chunk for memo recording (binary) */
  MEMO_AUDIO_CHUNK = 'memo_audio_chunk',
  /** Client → Server: end of memo recording */
  MEMO_AUDIO_END = 'memo_audio_end',
  /** Server → Client: memo saved confirmation with metadata */
  MEMO_SAVED = 'memo_saved',
  /** Server → Client: memo transcription complete */
  MEMO_TRANSCRIBED = 'memo_transcribed',
  /** Server → Client: memo AI processing complete */
  MEMO_PROCESSED = 'memo_processed',
  /** Server → Client: memo was auto-pinned to plan after transcription */
  MEMO_AUTO_PINNED = 'memo_auto_pinned',
  /** Server → Client: memo operation error */
  MEMO_ERROR = 'memo_error',

  // ── Segment TTS (pull model) ─────────────────────────────────────────────
  // Generic per-segment text→audio, used by BOTH the doc viewer and per-message
  // chat read-aloud (hence "SEGMENT", not "DOC"). The enum KEYS were renamed
  // TTS_DOC_* → TTS_SEGMENT_* for clarity (2026-06-16); the wire VALUES are
  // deliberately FROZEN as 'tts_doc_*' — renaming them would buy zero
  // developer-facing clarity while (a) opening a deploy-skew window where a
  // stale browser tab emits the old value to a new backend (silent no-op) and
  // (b) breaking the wire-faithful SEs (setpoint-evals/tts-document + the
  // chat-read-aloud simulator) that match on these literal strings. Do NOT
  // "tidy up" the values to match the keys.
  /** Client → Server: synthesize one text segment { text, index } */
  TTS_SEGMENT_SYNTH = 'tts_doc_synth',
  /** Server → Client: audio for a synthesized segment { index, audioBase64, durationMs } */
  TTS_SEGMENT_AUDIO = 'tts_doc_audio',
  /** Server → Client: synthesis error for a segment { index, message } */
  TTS_SEGMENT_ERROR = 'tts_doc_error',
  /** Server → Client: voice bug transcription complete — payload includes transcript + intent */
  BUG_TRANSCRIBED = 'bug_transcribed',

  // Autonomous Pipeline events
  /** Server → Client: chunk execution status changed */
  CHUNK_STATUS = 'chunk_status',
  /** Server → Client: all chunks in a plan reached terminal state */
  BATCH_COMPLETE = 'batch_complete',
  /** Server → Client: daily cost cap reached, queue paused */
  COST_CAP_REACHED = 'cost_cap_reached',
  /** Server → Client: queue state changed (paused/resumed) */
  QUEUE_STATE = 'queue_state',
  /** Server → Client: branch merge/discard/resolve result */
  BRANCH_STATUS = 'branch_status',
  /** Server → Client: phase PR created (includes prNumber + prUrl) */
  PHASE_PR_CREATED = 'phase_pr_created',
  /** Server → Client: phase CI failed or timed out — user intervention required */
  PHASE_CI_FAILED = 'phase_ci_failed',
  /** Server → Client: phase fully merged (AUTO_MERGE_PHASES path) */
  PHASE_MERGED = 'phase_merged',
  /** Server → Client: all phases complete — plan fully executed */
  PHASE_COMPLETE = 'phase_complete',

  // Wave execution events (multi-plan parallel orchestration)
  /** Server → Client: wave execution started — N plans grouped into waves */
  WAVE_STARTED = 'wave_started',
  /** Server → Client: a wave completed, next wave dispatched */
  WAVE_ADVANCED = 'wave_advanced',
  /** Server → Client: all waves complete — entire wave execution done */
  WAVE_COMPLETE = 'wave_complete',
  /** Server → Client: a plan-phase failed CI — wave paused */
  WAVE_FAILED = 'wave_failed',
  /** Server → Client: phase merged but next phase requires deployment first */
  PHASE_DEPLOY_REQUIRED = 'phase_deploy_required',
  /** Server → Client: deployment detected — deploy gate cleared, resuming */
  PHASE_DEPLOY_DETECTED = 'phase_deploy_detected',
  /** Server → Client: lifecycle event injected into the lighthouse orchestrator session */
  WAVE_SESSION_MESSAGE = 'wave_session_message',
  /** Server → Client: phase PR is awaiting manual merge — merge gate polling started */
  PHASE_MERGE_GATE_STARTED = 'phase_merge_gate_started',
  /** Server → Client: phase PR was manually merged — merge gate cleared, wave advancing */
  PHASE_MERGE_GATE_CLEARED = 'phase_merge_gate_cleared',

  // Context management events
  /** Server → Client: context usage exceeded threshold, auto-docs-update triggered */
  CONTEXT_HIGH = 'context_high',
  /** Server → Client: auto-docs-update completed */
  CONTEXT_DOCS_UPDATED = 'context_docs_updated',
  /** Server → Client: auto-continuation started after session reset */
  AUTO_CONTINUATION = 'auto_continuation',

  // Knowledge tidy events
  /** Server → Client: tidy proposals available for review */
  TIDY_PROPOSALS = 'tidy_proposals',

  // Provider failover events
  /** Server → Client: AI provider was switched (failover or manual) */
  PROVIDER_SWITCHED = 'provider_switched',
  /** Server → Client: health status of all AI providers */
  PROVIDER_STATUS = 'provider_status',
  /** Server → Client: provider error (rate limit exhausted) with optional fallback offer */
  PROVIDER_ERROR = 'provider_error',

  // Query health check (busy timeout keep-alive)
  /** Client → Server: check if a session's query is still running */
  CHECK_QUERY_HEALTH = 'check_query_health',
  /** Server → Client: query health status response */
  QUERY_HEALTH_STATUS = 'query_health_status',

  // SDK telemetry events
  /** Server → Client: approaching or hit API rate limit */
  RATE_LIMIT_WARNING = 'rate_limit_warning',
  /** Server → Client: cascade classifier fell back to regex (Haiku unavailable); Sonnet continues normally */
  CASCADE_DEGRADED = 'cascade_degraded',
  /** Server → Client: cascade classifier recovered after being degraded */
  CASCADE_RECOVERED = 'cascade_recovered',
  /** Server → Client: long-running tool progress (elapsed time) */
  TOOL_PROGRESS = 'tool_progress',
  /** Server → Client: agent hit max turns or other SDK error condition */
  SDK_ERROR = 'sdk_error',
  /**
   * Server → Client: a backend-emitted, persisted, role='system' note for the
   * chat timeline. Use for silent-stop recovery, 2-error abort, and similar
   * graceful-recovery events that need to land as a visible pill in the chat
   * (rendered via humanizeSystemEvent like any other system message).
   * Phase 2 of plans/gemini-tool-bridge-hardening.md.
   */
  SYSTEM_NOTE = 'system_note',

  // Prompt intercept events (smart routing)
  /** Server → Client: prompt was handled by cheap/inline agent (not the session's AI) */
  SYSTEM_ACTION = 'system_action',
  /** Server → Client: auto-switch to a different session (voice session mgmt) */
  SWITCH_TO_SESSION = 'switch_to_session',
  /** Client → Server: request creation of a new session */
  NEW_SESSION = 'new_session',
  /** Server → Client: new session created and should auto-switch */
  NEW_SESSION_CREATED = 'new_session_created',
  /** Server → Client: a response was shared to this user/session */
  MESSAGE_SHARED = 'message_shared',

  // Cross-model consultation live indicator
  /** Server → Client: a cross-model consultation has started (provider + model are working) */
  CONSULTATION_START = 'consultation_start',

  // Opus consultation events (unified selection mode)
  /** Server → Client: classifier recommends Opus for this prompt; prompt is HELD (not sent to Sonnet) */
  OPUS_SUGGESTED = 'opus_suggested',
  /** Client → Server: user confirmed Opus consultation with selected message context */
  OPUS_CONSULT = 'opus_consult',
  /** Client → Server: user dismissed the Opus suggestion (prompt released to Sonnet) */
  OPUS_DISMISSED = 'opus_dismissed',

  // Model registry events
  /** Server → Client: model catalog changed (new models discovered or models retired) */
  MODEL_CATALOG_UPDATED = 'model_catalog_updated',

  // Managed workspace provisioning events
  /** Server → Client: progress update during workspace provisioning */
  WORKSPACE_PROVISION_PROGRESS = 'workspace:provision_progress',
  /** Server → Client: workspace provisioning completed successfully */
  WORKSPACE_READY = 'workspace:ready',
  /** Server → Client: workspace provisioning failed */
  WORKSPACE_ERROR = 'workspace:error',
  /** Server → Client: progress update while cloning GitHub repos into sandbox */
  WORKSPACE_CLONE_PROGRESS = 'workspace:clone_progress',

  // Context tag
  /** Server → Client: session context tag updated (1-5 word summary of current work) */
  CONTEXT_TAG_UPDATED = 'context_tag_updated',

  // Latest captured intent (model-meta-hardening Phase 5)
  /** Server → Client: the latest turn's cascade work-category intent for this
   *  session, emitted at turn-end alongside the assistant message persist.
   *  Payload: { sessionId, intentType, model } — `intentType` is the raw enum
   *  (dev_work | deep_reasoning | cheap_task | …); the top bar maps it to a
   *  friendly label + shows the per-turn model. Mirrors CONTEXT_TAG_UPDATED. */
  LATEST_INTENT_UPDATED = 'latest_intent_updated',

  // Active project detection
  /** Server → Client: detected active project(s) updated for this session */
  PROJECTS_DETECTED = 'projects_detected',
  /** Client → Server: pin a specific project as primary for this session */
  PIN_PROJECT = 'pin_project',
  /** Client → Server: unpin the currently pinned project */
  UNPIN_PROJECT = 'unpin_project',
  /** Client → Server: pin a session to the top of the session list */
  PIN_SESSION_TO_LIST = 'pin_session_to_list',
  /** Client → Server: unpin a session from the top of the session list */
  UNPIN_SESSION_FROM_LIST = 'unpin_session_from_list',
  /** Client → Server: reset context boundary in-place (stay in same session) */
  RESET_CONTEXT = 'reset_context',
  /** Server → Client: context reset in progress — persisting knowledge before clearing */
  CONTEXT_RESETTING = 'context_resetting',
  /** Server → Client: context boundary has been set; older messages excluded from AI */
  CONTEXT_RESET = 'context_reset',

  /** Server → Client: agent may be in a loop — same tool called 3x identically */
  LOOP_SUSPECTED = 'loop_suspected',

  /** Server → Client: user triggered a browser intent but has not accepted the consent copy yet */
  BROWSER_CONSENT_REQUIRED = 'browser_consent_required',

  /** Server → Client: user triggered a browser intent but their tier doesn't include the browser tool */
  BROWSER_TIER_GATE = 'browser_tier_gate',

  /** Server → Client: browser intent cleared the gates but the shared Chrome is held by another of
   *  this user's sessions (resource-lease §5c). Payload: { sessionId, heldBy?, expiresAt?, queuePos? }.
   *  The agent should poll/queue rather than clobber the live holder. */
  BROWSER_BUSY = 'browser_busy',

  /** Server → Client: a gated feature was denied by FeatureGateService.
   *  Payload mirrors GateResult: { reason, featureKey, upgradeTo?, currentTier?, quota?, profileFlag? }. */
  FEATURE_GATED = 'feature_gated',

  /** Server → Client (multi-device fan-out): the user's prepaid credit balance changed —
   *  top-up credited, hold opened, settle burned, or release returned (valko-credits-platform
   *  Phase 2). Drives the live header balance pill + low-balance notices.
   *  Payload: CreditBalancePayload. */
  CREDIT_BALANCE = 'credit_balance',

  /** Server → Client (multi-device fan-out): the user's preferences/settings
   *  were mutated. Emitted to the user's `user:${uid}` room after any successful
   *  settings.controller mutation. Frontend handler debounces 500ms and calls
   *  useSettingsStore.getState().loadFromBackend() to refresh.
   *  Payload: { scope, changedKeys? } — scope is 'memo' | 'direct' | 'consent'.
   *  Phase 5 of settings-zustand-migration. */
  PREFERENCES_UPDATED = 'preferences_updated',

  /** Server → Client (multi-device fan-out): progress of an agentic workflow run
   *  ("Agent Runs"). ONE event carrying a discriminated `kind` union — see
   *  `workflow-progress.ts` for the full grammar (run/node lifecycle + the
   *  server-authoritative `run_snapshot` reconcile feed). Engine-agnostic: the Claude
   *  Agent SDK's Workflow tool today, the DTM engine later.
   *  Payload: WorkflowProgressEvent. */
  WORKFLOW_PROGRESS = 'workflow_progress',

  /** Client → Server: control a workflow run — kill / resume / dismiss / request-snapshot.
   *  Payload: WorkflowKillMessage | WorkflowResumeMessage | WorkflowDismissMessage |
   *  WorkflowRequestSnapshotMessage, discriminated by the event name below. */
  WORKFLOW_KILL = 'workflow_kill',
  WORKFLOW_RESUME = 'workflow_resume',
  WORKFLOW_DISMISS = 'workflow_dismiss',
  WORKFLOW_REQUEST_SNAPSHOT = 'workflow_request_snapshot',
}

/**
 * Payload shape for PREFERENCES_UPDATED events (Server → Client).
 * `scope` distinguishes the slice that changed — 'memo' covers PUT /settings/memo
 * (the bulk-edit endpoint), 'direct' covers per-column setters (gemini-deep-mode,
 * stt-correction-*, prefer-whisper, auto-submit-voice, etc.),
 * 'consent' covers POST /settings/consent.
 * `changedKeys` is diagnostic only — frontend always re-fetches the full
 * settings blob via loadFromBackend(), so partial-update logic isn't needed.
 */
export interface PreferencesUpdatedPayload {
  scope: 'memo' | 'direct' | 'consent';
  changedKeys?: string[];
}

/**
 * Payload shape for CREDIT_BALANCE events (Server → Client).
 *
 * Server-authoritative snapshot of the user's prepaid-credit wallet in CENTICREDITS
 * (1 credit = 100 centicredits). Emitted to the user's room on ledger mutations
 * (top-up, hold, settle/burn, release, clawback, expiry) — the frontend
 * creditsStore treats this as the reconcile source and never synthesizes balances
 * ([[feedback_server_state_is_authoritative]]).
 */
export interface CreditBalancePayload {
  /** Current balance = SUM(credit_ledger.centicredits), holds included. */
  balanceCenti: number;
  /** Centicredits currently reserved by open (unreleased) holds. */
  heldCenti: number;
  /** Signed change that triggered this emit (omitted for pure snapshots/reconciles). */
  deltaCenti?: number;
  /** What changed — e.g. 'topup', 'hold', 'settle', 'release', 'expiry', 'reconcile'. */
  reason?: string;
}

/**
 * Payload shape for PROVIDER_ERROR events (Server → Client).
 *
 * Emitted when an AI provider error is terminal for the turn (Gemini retries
 * exhausted, or an Anthropic auth/credit failure). For Anthropic credential
 * problems the gateway sets `authRequired:true` + `remedy:'claude_login'` and a
 * JSON-free `message`, so the frontend renders an actionable guidance card
 * instead of the raw error dump. voice-assistant#1057 (Part of #1052).
 */
export interface ProviderErrorPayload {
  sessionId?: string;
  /** 'google' (Gemini) or 'anthropic' (Claude). */
  provider: string;
  /** HTTP-ish status code (e.g. 401, 429) — best-effort, may be 0 when unknown. */
  code: number;
  /** Human-readable, JSON-free message safe to show the user. */
  message: string;
  /** How many automatic retries were attempted before giving up. */
  retried: number;
  /** When true, the frontend may offer a "Try with <other provider>" button. */
  offerFallback: boolean;
  /**
   * When true, this is an Anthropic credential/credit failure the USER must fix
   * (vs. a transient error). The frontend renders a guidance card, not a banner.
   */
  authRequired?: boolean;
  /** Remedy hint the frontend maps to actionable steps. */
  remedy?: 'claude_login';
}

/** Payload shape for AUDIO_CHUNK events (all fields optional — backward compatible).
 *
 * The chunk bytes travel base64-encoded in `chunkB64` (a STRING) rather than as a
 * binary attachment. A pure-JSON payload makes socket.io emit a plain EVENT packet
 * instead of a BINARY_EVENT, which removes the binary-attachment reassembly desync
 * that corrupted prod audio buffers on 2026-06-14 (see backend audio-chunk-codec.ts /
 * _incidents/2026-06-14-voice-socketio-framing-corruption.md). */
export interface AudioChunkPayload {
  /** UUID v4 minted by the frontend at start() time. Absent from legacy clients. */
  recordingId?: string;
  /** Monotonic per-recording chunk index (incremental path) — enables ordered
   *  reassembly + gap detection in the per-recording staging buffer. Absent on
   *  combined-blob sends and legacy clients. */
  index?: number;
  /** Base64-encoded chunk bytes — the desync-proof transport for chunk data. */
  chunkB64?: string;
  /** @deprecated Legacy binary-attachment form — retained so stale PWA clients on
   *  cached JS keep working. New clients send `chunkB64` instead. */
  chunk?: ArrayBuffer;
}

/** Payload shape for AUDIO_END events (recordingId is optional — backward compatible) */
export interface AudioEndPayload {
  /** UUID v4 minted by the frontend at start() time. Absent from legacy clients. */
  recordingId?: string;
  images?: unknown[];
  language?: string;
  sessionId?: string;
  transcribeOnly?: boolean;
  /** UUID v4 minted by the frontend for outbound ack tracking. Absent from legacy clients. */
  clientMsgId?: string;
}

/** Payload shape for MESSAGE_RECEIVED events (Server → Client). */
export interface MessageReceivedPayload {
  clientMsgId: string;
  /** DB UUID of the persisted chat_message row. */
  serverMsgId: string;
  sessionId: string;
}

/**
 * Payload shape for PROMPT_QUEUED events (Server → Client).
 * Emitted when a mid-turn prompt is accepted into the DB-backed queue instead of being
 * refused with "Still processing previous request" (session-inflight-state-integrity Phase 1).
 */
export interface PromptQueuedPayload {
  event: WsEvent.PROMPT_QUEUED;
  sessionId: string;
  clientMsgId?: string;
  /** FIFO position of this row; -1 when the insert was deduped (client retry). */
  position: number;
  /** Rows queued for this session, including this one. */
  queueDepth: number;
}

/**
 * Why the server reports a session idle without a terminal frame (SESSION_IDLE). One typed
 * discriminator per DISTINCT condition — never one alarming string for three different things
 * (docs/frontend-fsm-pattern.md § Server-authoritative reconciliation).
 */
export type SessionIdleReason =
  /** The handler that took a bare intake claim gave it back (AUDIO_END exit, empty prompt, lost drain race). */
  | 'claim_released'
  /** Nobody released a bare claim; the 10 s TTL / the 60 s sweep healed it. */
  | 'claim_expired'
  /** A live pipeline went silent for the idle-eviction window and the sweep reaped it (a zombie). */
  | 'evicted';

/**
 * Payload shape for SESSION_IDLE events (Server → Client).
 * The server freed `sessionId`'s busy-slot with no terminal frame; a page that holds a
 * SERVER-armed busy state for it (a synthetic `server-*` entry — never a local send) clears it.
 */
export interface SessionIdlePayload {
  sessionId: string;
  reason: SessionIdleReason;
}

/**
 * Payload shape for CANCEL_PROMPT events (Client → Server). Both fields optional — the legacy
 * shapes (`undefined`, `{ sessionId }`) are unchanged.
 */
export interface CancelPromptPayload {
  /** The session whose running turn to abort (per-session; absent = the user-wide abort). */
  sessionId?: string;
  /**
   * `clientMsgId`s of the prompts THIS composer holds in `queued` mode for `sessionId` and is
   * WITHDRAWING. The server dequeues exactly those rows (caller's userId + session + still
   * `status='queued'`) BEFORE it aborts the running turn, so the aborted turn's slot-release
   * cannot drain a prompt the operator just cancelled. A local-only clear of the composer entry
   * would leave the queue ROW alive (voice-assistant#2174, defect 3).
   */
  withdrawClientMsgIds?: string[];
}

/**
 * Payload shape for SESSIONS_RECONCILE events (Server → Client).
 * Phase 2 (composer-state-machine) adds `lastReceivedClientMsgIds`.
 * Phase 2 (session-load-fsm-migration) adds `openSessionIds`.
 */
export interface SessionsReconcilePayload {
  activeQuerySessionIds: string[];
  /** clientMsgIds the server persisted in the last hour — used to reconcile lost outbound sends. */
  lastReceivedClientMsgIds?: string[];
  /**
   * All non-cleared session IDs belonging to this user.
   * Used by sessionLoadStore.applyReconcile() to load sessions that are not in the
   * frontend's saved-tabs list (e.g. URL deep-link sessions, cross-device sync).
   * Capped at 50 most-recently-updated sessions to keep the payload small.
   */
  openSessionIds?: string[];
}

/**
 * Why partialText survived to the LOAD_TAB_MESSAGES reconnect path.
 * Set at the interruption site so the frontend can render the right framing.
 * Default (no reason set) → `'unknown'` — neutral "connection lost mid-response" framing.
 * Do NOT default to `container_restart`: that synthesizes a false "Server restarted"
 * claim for ordinary WS-disconnect / SDK-subprocess-exit interruptions (RestartCount=0).
 * `container_restart` is reserved for a real boot-id-change detector (not built yet).
 */
export type PartialReason =
  | 'context_reset'     // context-threshold SDK reset; agent continued in new window
  | 'handoff'           // /handoff skill triggered; existing HandoffDivider shows this
  | 'zombie_eviction'   // 12-min hard-TTL eviction; query abandoned
  | 'auto_continuation' // WhatsApp-style auto-compaction continuation
  | 'sdk_crash'         // SDK subprocess OOM / stdin JSON parse error
  | 'hot_reload'        // backend dev hot-reload (dev environment only)
  | 'container_restart' // container restart / OOM kill — process ended before cleanup
  | 'unknown';          // fallback when the source is not instrumented yet

export interface TranscriptionMessage {
  event: WsEvent.TRANSCRIPTION;
  text: string;
  /** STT post-processing metadata (present when Whisper path ran through SttCorrectionService). */
  correction?: {
    provider: 'gemini' | 'regex' | 'none';
    latencyMs: number;
    changed: boolean;
  };
}

export interface ClaudeChunkMessage {
  event: WsEvent.CLAUDE_CHUNK;
  text: string;
}

export interface ClaudeDoneMessage {
  event: WsEvent.CLAUDE_DONE;
  fullText: string;
  /**
   * True when the backend is running in sim tier (VOICE_SIM_MODE=on).
   * Constant for the lifetime of the backend process; surfaced on every
   * CLAUDE_DONE so SE harnesses, frontend telemetry, and reloaded message
   * rendering can distinguish sim turns from real turns without re-querying.
   */
  isSimulated?: boolean;
  /**
   * Session-cumulative totals AFTER this turn's accumulation (Phase 3,
   * session-inflight-state-integrity). Carried ONLY on the main turn-completion
   * emit — patches the live cost/turns pill without a reload. Distinct from any
   * per-query numTurns/totalCostUsd carried elsewhere on this event.
   */
  sessionNumTurns?: number;
  sessionTotalCostUsd?: number;
}

export interface ErrorMessage {
  event: WsEvent.ERROR;
  message: string;
}

export type ServerMessage =
  | TranscriptionMessage
  | ClaudeChunkMessage
  | ClaudeDoneMessage
  | ErrorMessage;

/** Image attachment sent from client to server */
export interface ImageAttachment {
  /** Base64-encoded image data (no data URI prefix) */
  data: string;
  /** MIME type of the image */
  mimeType: 'image/jpeg' | 'image/png' | 'image/gif' | 'image/webp';
  /** Original file name */
  fileName: string;
  /** Smaller base64 for chat display (data URI with prefix) */
  thumbnailDataUri?: string;
}

/** Quick-link for the frontend panel */
export interface QuickLink {
  label: string;
  url: string;
}

/** Session summary for the sessions drawer */
export interface SessionSummary {
  id: string;
  name: string | null;
  status: string;
  numTurns: number;
  totalCostUsd: number | null;
  createdAt: string;
  updatedAt: string;
  previewText: string | null;
  model: string | null;
  isRelay: boolean;
  /** AI provider that ran this session (anthropic | google) */
  provider: AiProviderType | null;
  /** Last known input tokens (for context % calculation) */
  lastInputTokens: number | null;
  /** Context window size for the model */
  lastContextWindow: number | null;
  /** Rolling per-turn signpost summary (auto-generated by Gemma after each turn) */
  rollingSignpost: string | null;
  /** Ultra-compact context tag (1-5 words, Haiku-generated from last 2 user prompts) */
  contextTag: string | null;
  /** Linked plan ID for review sessions */
  linkedPlanId: string | null;
  /** Linked GitHub issue number (work item tracking) */
  linkedIssueNumber: number | null;
  /** GitHub repo for the linked issue */
  linkedIssueRepo: string | null;
  /** Auto-detected active project keys for this session (primary first, up to 3) */
  activeProjects: string[] | null;
  /** User-pinned project key (overrides auto-detection) */
  pinnedProject: string | null;
  /** Position in pinned-to-top list (null = not pinned, lower = higher in list) */
  listPinPosition: number | null;
  /** Active plan detected from current-plan.md in the primary project */
  activePlan: { title: string; project: string; command: string } | null;
  /** Timestamp of the last in-place context reset (messages before this are excluded from AI context) */
  contextResetAt: string | null;
  /** Session origin: 'interactive' | 'autonomous' | 'relay' */
  source: string;
  /** Plan chunk ID this autonomous session is executing (null for interactive/relay) */
  chunkId: string | null;
  /** Chunk title (populated for autonomous sessions via join) */
  chunkTitle: string | null;
  /** Plan name (populated for autonomous sessions via join) */
  planName: string | null;
  /** Reasoning effort tuning (Opus/Sonnet only). Null = SDK default. */
  effort?: EffortLevel | null;
  /** 1M context window opt-in. Sonnet 4.6 / Opus 4.7. */
  enable1mContext?: boolean;
  /** Admin-only: impersonating another user's AKB perspective */
  actAsPersona?: string | null;
  /**
   * Latest turn's cascade work-category intent (model-meta-hardening Phase 5).
   * Read from the most recent `assistant` chat_message row's `intentType`
   * (server-authoritative — same row the live `LATEST_INTENT_UPDATED` event
   * derives from). Raw enum value (`dev_work` | `deep_reasoning` | `cheap_task`
   * | `session_mgmt` | …); the top bar maps it to a friendly label. Null until
   * the session has at least one classified assistant turn.
   */
  latestIntentType?: string | null;
  /**
   * Model that ran the latest assistant turn (per-turn `chat_message.model`, NOT
   * the session default `model`). Rendered alongside the friendly intent label.
   */
  latestIntentModel?: string | null;
}

/** Work item from GitHub Issues or PRs */
export interface WorkItemSummary {
  number: number;
  title: string;
  state: string;
  type: 'feat' | 'fix' | 'chore' | 'unknown';
  labels: string[];
  url: string;
  createdAt: string;
  updatedAt: string;
  body: string;
  /** 'issue' or 'pr' */
  kind: 'issue' | 'pr';
  /** PR-specific merged/closed/open state */
  prState?: 'open' | 'closed' | 'merged';
  /** Full repo slug, e.g. 'inctasoft/voice-assistant' */
  repo: string;
  /** Short display name, e.g. 'voice-assistant' */
  repoSlug: string;
}

/** Work items grouped by repository */
export interface WorkItemGroup {
  repo: string;
  repoSlug: string;
  items: WorkItemSummary[];
  refreshedAt: string;
}

/** Search result from hybrid FTS + semantic search */
export interface SearchResult {
  messageId: string;
  sessionId: string;
  sessionName: string | null;
  role: string;
  text: string;
  score: number;
  createdAt: string;
}

/**
 * Honesty label for a context-occupancy reading (kimi-tool-parity.md Phase 0a/0b).
 * - `provider-last-prompt` — the provider's own last prompt-token count (Kimi/Gemini: the
 *   prompt already includes cached tokens — the "inclusive" dialect).
 * - `sdk-last-assistant` — folded from the SDK's last assistant-message usage (Claude:
 *   input + cache_creation + cache_read — the "additive" dialect).
 * - `stale-anchor` — a number exists but the turn did NOT end on it (usage-less final
 *   round-trip, loop-ceiling/malformed-streak/abort exit). Resolves to unmetered, NEVER
 *   to a measured percentage.
 * - `unmetered` — no usage was reported at all.
 */
export type ContextTokensSource = 'provider-last-prompt' | 'sdk-last-assistant' | 'stale-anchor' | 'unmetered';

/** Where the context-window denominator came from. */
export type ContextWindowProvenance = 'declared' | 'sdk-reported' | 'blended';

/**
 * The ONE occupancy shape the wire carries (server-computed; clients render, never derive).
 * Exactly five kinds — `stale-anchor` is deliberately NOT a kind: providers report it as a
 * SOURCE and the ledger maps it to `{ kind: 'unmetered', reason: 'stale-anchor' }`.
 */
export type ContextFullness =
  | { kind: 'pending' }
  | { kind: 'measured'; contextTokens: number; contextWindow: number; pct: number;
      source: 'provider-last-prompt' | 'sdk-last-assistant'; windowProvenance: ContextWindowProvenance }
  | { kind: 'projected'; contextTokens: number; contextWindow: number; pct: number; anchorTokens: number }
  | { kind: 'unknown-window'; contextTokens: number; source: 'provider-last-prompt' | 'sdk-last-assistant' }
  | { kind: 'unmetered'; reason: 'no-usage' | 'stale-anchor' };

/** Tool activity event from Claude Agent SDK */
export interface ToolActivityMessage {
  type: 'start' | 'end' | 'progress' | 'context';
  tool: string;
  input?: string;
  /** Elapsed seconds for progress events */
  elapsedSeconds?: number;
  /** Mid-turn context occupancy anchor (type 'context' events) — last measured prompt tokens. */
  contextTokens?: number;
  contextTokensSource?: ContextTokensSource;
  /** Server-computed display-only projection between anchors (anchor + appendedChars/4).
   *  NEVER reaches control (threshold/reset) or billing seams. */
  projectedContextTokens?: number;
}

/**
 * Server → Client payload for `WsEvent.SYSTEM_NOTE`.
 * Emitted from the backend when a graceful-recovery event needs to land as a
 * persisted role='system' message in the chat (silent-stop, 2-error abort, etc.).
 * Phase 2 of plans/gemini-tool-bridge-hardening.md.
 */
export interface SystemNoteMessage {
  sessionId: string;
  text: string;
  /** Optional reason tag — useful for frontend humanizeSystemEvent routing. */
  reason?: 'silent_stop' | 'tool_error_abort' | 'tool_timeout' | 'tool_denial_abort';
}

/** SSH target configuration */
export interface SshTarget {
  id: string;
  label: string;
  host: string;
  port: number;
  user: string;
  /** True when this target is the same machine running the voice-assistant backend.
   *  Used to keep command drawer and status info visible in SSH mode. */
  isSelf?: boolean;
}

/**
 * Server → Client payload for `WsEvent.SSH_STATE` — the server-authoritative
 * reconcile signal emitted on the Socket.IO handshake (and on demand).
 *
 *   attached:true   a live PTY exists server-side; `target` labels it and
 *                   `replay` is its full output buffer (re-sourced as the
 *                   terminal scrollback, replacing the client's stale copy).
 *   attached:false  no live PTY for this user → a detached client drops to idle.
 *
 * Only the durable host/local PTY survives a socket drop (own-box SSH is killed
 * on disconnect), so `sessionType` is 'local' in v1.
 *
 * `seq` (Phase 4) is the high-water output cursor the `replay` buffer represents;
 * the client adopts it as `lastSeq` so live chunks already in the buffer
 * (seq <= this) are deduped on reattach rather than double-appended.
 */
export interface SshStatePayload {
  terminalId: string;
  attached: boolean;
  sessionType?: 'local' | 'ssh';
  target?: SshTarget;
  /** Full server-side PTY output buffer for reattach replay (attached:true). */
  replay?: string;
  /** High-water output cursor the `replay` buffer represents (Phase 4). */
  seq?: number;
}

/**
 * Server → Client payload for `WsEvent.SSH_OUTPUT` — a single PTY output chunk.
 * `seq` is a per-terminal monotonic cursor (Phase 4): the client drops any chunk
 * with `seq <= lastSeq` (idempotent append), so reattach replay and racing live
 * chunks dedupe deterministically. `seq` is OPTIONAL — a chunk without it degrades
 * to plain monotonic append (backward-compat with pre-Phase-4 emitters).
 */
export interface SshOutputPayload {
  data: string;
  seq?: number;
}

/** Project tag for the interactive project bar */
export interface ProjectTag {
  key: string;
  label: string;
  url: string | null;
  aliases: string[];
  description: string;
}

/** Supported AI provider types */
export type AiProviderType = 'anthropic' | 'openai' | 'google' | 'qwen' | 'agy' | 'claude-cli' | 'local-5090' | 'kimi';

/** Provider health status for UI display */
export interface ProviderStatusInfo {
  provider: AiProviderType;
  healthy: boolean;
  lastError?: string;
  lastErrorAt?: string;
}

/** Active provider info sent to frontend */
export interface ActiveProviderInfo {
  provider: AiProviderType;
  model: string;
  autoMode: boolean;
  reason?: string;
  providers: ProviderStatusInfo[];
}

export interface ProvisionProgressEvent {
  step: 'creating_container' | 'injecting_key' | 'waiting_ssh' | 'verifying_tools' | 'configuring' | 'writing_caddy';
  message: string;
  progress: number; // 0-100
}

export interface DevSlotInfo {
  slot: 'a' | 'b' | 'c';
  port: number;
  url: string;
}

export interface WorkspaceReadyEvent {
  machineName: string;
  host: string;
  port: number;
  devSlots?: DevSlotInfo[];
}

export interface WorkspaceErrorEvent {
  step: string;
  error: string;
}

export interface WorkspaceCloneProgressEvent {
  repo: string;
  status: 'cloning' | 'done' | 'error';
  message: string;
}

/** User settings response from GET /settings */
export interface UserSettings {
  // preferredProvider retired (model-meta-hardening Phase 6b) — provider is
  // derived per-session from the session model id; no per-user default.
  keys: { provider: AiProviderType; hasKey: boolean }[];
  memoSettings: UserPreferences;
  /** Whether server-side Whisper STT is enabled (deployment-time flag) */
  whisperEnabled: boolean;
  /** Consent timestamps — null means not yet accepted */
  consent: {
    tosAcceptedAt: string | null;
    privacyAcceptedAt: string | null;
    agentAccessConsentedAt: string | null;
    /** Protofac credit-disclosure acknowledgement timestamp (null = not acknowledged) */
    creditConsentedAt: string | null;
    /** Version of the consent bundle the user accepted (null = pre-versioning) */
    consentVersion: string | null;
    /** True when the accepted consent bundle matches the current version (re-gate when false) */
    upToDate: boolean;
  };
  /** ISO-3166-1 alpha-2 signup country (null = not declared → Protofac geo gate shows) */
  country: string | null;
  /** GitHub connection status */
  github: {
    connected: boolean;
    login?: string;
    avatarUrl?: string;
    oauth?: { connected: boolean; login?: string; avatarUrl?: string; connectedAt?: string };
    pat?: { connected: boolean; login?: string; tokenPreview?: string; connectedAt?: string };
    activeMethod?: 'pat' | 'oauth' | 'none';
  };
  /** Feature flags from InstallationProfile */
  githubFeatures?: {
    oauthEnabled: boolean;
    patEnabled: boolean;
  };
  /** Build version baked at image build time — PR#N or git SHA. Later: semver. */
  buildVersion?: string;
  /** For managed tier users: current provisioning state */
  provisioningStatus?: string;
  /** User subscription tier (quota axis) */
  tier?: string;
  /** User role (authority axis): 'admin' | 'viewer' */
  role?: string;
  /** Whether to always load Playwright browser tools on session start (user opt-in) */
  browserAlwaysOn?: boolean;
  /** Gemini deep-context mode (Phase 3 of gemini-context-maximization plan).
   *  'off' = none, 'detected' = active project's CLAUDE.md (default), 'all' = every workspace CLAUDE.md. */
  geminiDeepMode?: 'off' | 'detected' | 'all';
  /** Per-user STT correction override. null = follow profile flag; false = opt out. */
  sttCorrectionEnabled?: boolean | null;
  /** When true, Gemini Flash uses thinking mode (thinkingBudget: 2048) for STT correction.
   *  null/false = thinking disabled (default — faster and cheaper). */
  sttCorrectionThinking?: boolean | null;
  /** When true, always use Whisper STT instead of the device-native Web Speech API. */
  preferWhisper?: boolean | null;
  /** When true, automatically send transcribed text immediately after Whisper finishes. */
  autoSubmitVoice?: boolean | null;
  /** Whether Wave Coordinator feature is enabled on this deployment */
  waveExecutionEnabled?: boolean;
  /** Per-project autonomous execution overrides. null = all projects inherit global flag. */
  perProjectAutonomousOverrides?: Record<string, boolean> | null;
  /** Illustrative "Auto" turn-budget hint for the Settings chat-turns dropdown: what a fresh
   *  (0 tokens used) interactive session on the largest supported context window (1M) resolves
   *  to today. Computed live from `backend/src/voice/turn-budget.ts` — never a hardcoded
   *  literal (that was the Lane 3 va-uiux-hardening-wave-one defect: a stale pre-#666 value). */
  autoTurnBudgetHint?: number;
}

/** Voice memo summary for the memos list */
export interface VoiceMemoSummary {
  id: string;
  title: string | null;
  status: string;
  durationMs: number | null;
  audioSizeBytes: number;
  transcript: string | null;
  distilledTranscript: string | null;
  aiSummary: string | null;
  tags: string[];
  imageCount: number;
  projectTags: string[] | null;
  groupId: string | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Voice memo group summary */
export interface VoiceMemoGroupSummary {
  id: string;
  name: string;
  projectTags: string[];
  aiGroupPlan: string | null;
  planModel: string | null;
  planCostUsd: number | null;
  status: string;
  memoCount: number;
  summary: string | null;
  memos?: VoiceMemoSummary[];
  createdAt: string;
  updatedAt: string;
}

/** Plan chunk summary for the plans tab */
export interface PlanChunkSummary {
  id: string;
  title: string;
  description: string | null;
  effort: string | null;
  priority: number;
  sortOrder: number;
  status: string; // pending | in-progress | done | failed | needs-review | deferred
  riskLevel: string; // low | medium | high
  validation: string | null;
  autoComplete: boolean;
  phaseNumber: number | null;
  dependsOn: string[] | null;
  executionLog: string | null;
  modifiedFiles: string[] | null;
  executedAt: string | null;
  validatedAt: string | null;
  /** Observable session ID — the autonomous claude_session created for this chunk's execution */
  sessionId?: string | null;
}

/** Living project plan summary */
export interface ProjectPlanSummary {
  id: string;
  projectKey: string;
  name: string;
  source: string; // 'memo' | 'file'
  currentPlan: string | null;
  version: number;
  status: string; // active | stale | planning | archived
  executionStatus: string; // idle | executing | awaiting-merge
  planModel: string | null;
  planCostUsd: number | null;
  lastPlannedAt: string | null;
  totalMemos: number;
  pendingMemoCount: number;
  absorbedMemoIds: string[] | null;
  executionSessionId: string | null;
  dtmJobId: string | null;
  executionBranch: string | null;
  planFilePath: string | null;
  lastExecutedAt: string | null;
  createdAt: string;
  updatedAt: string;
  chunks: PlanChunkSummary[];
}

/**
 * Reasoning effort level for the Anthropic SDK `effort` parameter.
 * Mirrors `EffortLevel` in @anthropic-ai/claude-agent-sdk. Only Opus models
 * support 'max' — see ModelRegistry.capabilities.supportedEffortLevels.
 */
export type EffortLevel = 'low' | 'medium' | 'high' | 'xhigh' | 'max';

/** Configurable user preferences (stored per-user in DB) */
export interface UserPreferences {
  /** Model for group planning: 'sonnet' (default, cheap) or 'opus' (quality) */
  planModel: 'sonnet' | 'opus';
  /** Max lines of CLAUDE.md to inject per project (default 120) */
  contextLines: number;
  /** Max Opus planner turns. 0 = disabled (unlimited), -1 = auto (context-aware), 1-200 = explicit cap */
  maxPlannerTurns: number;
  /** Max Sonnet worker turns. 0 = disabled (unlimited), -1 = auto (context-aware), 1-200 = explicit cap */
  maxWorkerTurns: number;
  /** Max chat turns. 0 = disabled (unlimited), -1 = auto (context-aware), 1-200 = explicit cap */
  maxChatTurns: number;
  /** Whether to auto-triage after transcription (default true) */
  autoTriage: boolean;
  /** STT language for memo transcription (default 'en') */
  memoLanguage: string;
  /** Auto-pin transcribed memos to their project plan (skip manual 📌 step) */
  autoPin: boolean;
  /** Whether auto-replan triggers when threshold is reached (default true) */
  autoReplanEnabled: boolean;
  /** Number of pending memos that triggers auto-replan (default 5) */
  autoReplanThreshold: number;
  /** Whether to auto-trigger /docs-update when context exceeds threshold (default true) */
  autoDocsUpdate: boolean;
  /** Context % threshold that triggers auto-docs-update (default 65) */
  autoDocsUpdateThreshold: number;
  /** Whether to auto-continue after session reset (turn limit or context threshold) */
  autoContinueOnReset: boolean;
  /** Generate a structured handoff before auto-resetting the session (default true).
   *  Adds ~1 s and ~$0.001 per reset (one Haiku call). */
  handoffOnReset: boolean;
  /** Speech-to-text provider: 'phone' = browser Web Speech API, 'whisper' = server-side Whisper */
  speechProvider: 'phone' | 'whisper';
  /** Auto-play TTS when an agent response arrives (default true) */
  autoPlay: boolean;
  /** Operational Docs directory — agent-produced working files, reports, exports (e.g. "_operational_docs"). Scans each project dir for this subdir. Empty = disabled. */
  tempDocsDirName: string;
  /** Plans directory name (e.g. "plans"). Scans each project dir for this subdir. Empty = disabled. */
  plansDirName: string;
  /** Protofac sandbox: ABSOLUTE path to the user's plans folder INSIDE their sandbox (e.g. "/home/valko/myrepo/plans").
   *  On a sandbox-backed profile the Plans tab lists from here over SSH instead of the workspace. Empty = default. SE-PF6 (#1391). */
  plansSourceDir: string;
  /** Project Docs directory — stable reference docs, API guides, architecture notes (e.g. "docs"). Scans each project dir for this subdir. Empty = disabled. */
  projectDocsDirName: string;
  /** Archive directory name (e.g. "_archive"). Subdirs named after the other dirs (plans/, _operational_docs/, docs/) auto-split in the drawer. Empty = disabled. */
  archiveDirName: string;
  /** Registry directory — tests.yaml, services.yaml, per-project test specs (e.g. "registry"). Scans each project dir for this subdir. Empty = disabled. */
  registryDirName: string;
  /** Mermaid diagram color preset. 'auto' = follows app theme. */
  diagramStyle: 'auto' | 'minimal' | 'rich' | 'vibrant';
  /** Days to retain chat messages. 0 = keep forever. Default: 30. */
  messageRetentionDays: number;
  /**
   * Default reasoning effort applied when starting a new Opus session.
   * Models that support effort but no explicit default fall back to the SDK
   * default ('high'). See plans/opus-effort-slider.md.
   */
  defaultOpusEffort?: EffortLevel;
  /** Default reasoning effort applied when starting a new Sonnet session. */
  defaultSonnetEffort?: EffortLevel;
  /**
   * Set to true after the user has acknowledged the cost warning for the
   * 'max' effort level. Used to skip the confirmation modal on subsequent
   * selections.
   */
  acknowledgedMaxEffort?: boolean;
  /**
   * Default 1M-token context window setting applied to new sessions for
   * models whose `capabilities.supports1MContext` is true (Opus 4.7,
   * Sonnet 4.6). Frontend gates the toggle to paid tiers only.
   */
  default1mContextEnabled?: boolean;
  /**
   * Max tokens injected from the AKB DB context layer per query.
   * Default 4000. Lower values reduce prompt size; higher values include
   * more operator-curated knowledge per turn.
   */
  akbContextBudget?: number;
  /**
   * Max tokens injected from the semantic (vector) context layer per query.
   * Default 600. Raise to inject more relevant workspace knowledge; lower
   * to reduce prompt overhead.
   */
  semanticContextBudget?: number;
  /**
   * Embeddings A/B toggle (measurable, per-user). Gates DB-embeddings-backed
   * inference-time enrichment (`KnowledgeEmbeddingService.getRelevantContext()` /
   * `getUserLearnedContext()`) without touching write-path generation (30-min sync
   * cron, nightly tidy, all INSERTs) — those keep running unconditionally regardless
   * of this setting. 'on' = always enrich (default — no behavior change on rollout).
   * 'off' = never enrich (plain kill switch). 'auto' = randomize per turn, seeded
   * deterministically from the turn's `queryId`, to make the A/B measurable via
   * `turn_analytics.embeddingsVariant`. Read fresh every turn (uncached
   * `UserPreferences`, same store as `akbContextBudget`) — a flip takes effect on
   * the very next turn. See plans/embeddings-ab-toggle.md.
   */
  embeddingsAbMode?: 'on' | 'off' | 'auto';
  /**
   * Max output tokens for the Haiku prompt classifier.
   * Default 300. Only affects the routing call, not main agent responses.
   */
  classifierMaxTokens?: number;
  /**
   * Advisory output token budget for the main Claude session (taskBudget).
   * 0 = unlimited (SDK default). When non-zero, the SDK informs the model
   * of its remaining token budget so it can pace its response.
   */
  outputTokenBudget?: number;
  /**
   * When true (default), pressing OS back from any visited session lands
   * directly on the session list. Session-to-session swipes use replaceState
   * so only the most recent session occupies the chat history slot. When
   * false, every visited session pushes its own history entry — OS back
   * walks through them one by one before reaching the list.
   */
  osBackToSessionList?: boolean;
}

/** One group in a tree-mode file listing (e.g. all files inside voice-assistant/plans) */
export interface FileTreeGroup {
  label: string;
  basePath: string;
  files: { name: string; path: string; size: number; modifiedAt: string }[];
}

/** A single file entry used in workspace tree responses */
export interface WorkspaceFile {
  name: string;
  path: string;
  size: number;
  modifiedAt: string;
}

/** One project in the workspace tree — only included if it has ≥1 file in any section */
export interface WorkspaceProject {
  name: string;
  posts: WorkspaceFile[];
  opDocs: WorkspaceFile[];
  plans: WorkspaceFile[];
  projectDocs: WorkspaceFile[];
  registry: WorkspaceFile[];
  /** Uploaded documents converted to Markdown (from uploads/ dir, .md files only) */
  uploads: WorkspaceFile[];
  /** Files from archive dir whose first path segment matches plansDirName */
  archivedPlans: WorkspaceFile[];
  /** Files from archive dir whose first path segment matches opDocsDirName */
  archivedOpDocs: WorkspaceFile[];
  /** Files from archive dir whose first path segment matches projectDocsDirName */
  archivedProjectDocs: WorkspaceFile[];
  /** Files from archive dir root (no subdir) or unrecognised subdir */
  archivedOther: WorkspaceFile[];
}

/** Response from GET /files/workspace-tree */
export interface WorkspaceTreeResponse {
  workspaceRoot: {
    posts: WorkspaceFile[];
    opDocs: WorkspaceFile[];
    plans: WorkspaceFile[];
    projectDocs: WorkspaceFile[];
    registry: WorkspaceFile[];
    archive: WorkspaceFile[];
  };
  projects: WorkspaceProject[];
}

/** @deprecated Use UserPreferences */
export type MemoSettings = UserPreferences;

export const DEFAULT_USER_PREFERENCES: UserPreferences = {
  planModel: 'opus',
  contextLines: 120,
  maxPlannerTurns: 100,
  maxWorkerTurns: 100,
  maxChatTurns: 0,
  autoTriage: true,
  memoLanguage: 'en',
  autoPin: false,
  autoReplanEnabled: true,
  autoReplanThreshold: 5,
  autoDocsUpdate: true,
  autoDocsUpdateThreshold: 71,
  autoContinueOnReset: true,
  handoffOnReset: true,
  speechProvider: 'phone',
  autoPlay: true,
  tempDocsDirName: '',
  plansDirName: '',
  plansSourceDir: '',
  projectDocsDirName: '',
  archiveDirName: '',
  registryDirName: '',
  diagramStyle: 'auto',
  messageRetentionDays: 30,
  osBackToSessionList: true,
};

/** @deprecated Use DEFAULT_USER_PREFERENCES */
export const DEFAULT_MEMO_SETTINGS = DEFAULT_USER_PREFERENCES;

/** Period-specific stats */
export interface MemoPeriodStats {
  memos: number;
  recorded: number;
  transcribed: number;
  planned: number;
  groups: number;
  durationMs: number;
  costUsd: number;
}

/** Stats for the memos page header */
export interface VoiceMemoStats {
  total: number;
  recorded: number;
  transcribed: number;
  planned: number;
  totalDurationMs: number;
  totalSizeBytes: number;
  groupCount: number;
  groupsPlanned: number;
  today: MemoPeriodStats;
  week: MemoPeriodStats;
  totalPlanCostUsd: number;
}

// ─── Predefined Roles (compose panel role pills) ───────────────────────

export interface RoleDefinition {
  key: string;
  name: string;
  icon: string;
  content: string;
}

/**
 * Predefined roles available to all tiers.
 * Hardcoded constant — NOT stored in DB.
 * Used by frontend RolePillStrip and backend user-turn injection.
 */
export const PREDEFINED_ROLES: readonly RoleDefinition[] = [
  {
    key: 'architect',
    name: 'Architect',
    icon: '🏗️',
    content: 'You are a senior software architect. Focus on system design, scalability, separation of concerns, and long-term maintainability. Evaluate trade-offs explicitly. Suggest architectural patterns when relevant.',
  },
  {
    key: 'ux',
    name: 'UX Expert',
    icon: '🎨',
    content: 'You are a UX/UI expert. Focus on user experience, accessibility, mobile-first design, and intuitive interactions. Critique layouts, flows, and visual hierarchy. Suggest improvements grounded in real usage patterns.',
  },
  {
    key: 'debug',
    name: 'Debug',
    icon: '🔍',
    content: 'You are a debugging specialist. Be methodical: reproduce first, then isolate. Check logs, stack traces, and edge cases. Consider race conditions, state corruption, and environment differences. Never guess — verify.',
  },
  {
    key: 'security',
    name: 'Security',
    icon: '🔒',
    content: 'You are a security engineer. Audit for OWASP Top 10, injection, auth bypass, IDOR, XSS, CSRF, and data exposure. Think like an attacker. Flag risks with severity and suggest mitigations.',
  },
  {
    key: 'explain',
    name: 'Explain',
    icon: '📖',
    content: 'Explain clearly and concisely. Use analogies and examples. Assume the reader is smart but unfamiliar with this specific area. Build mental models, not just facts. Structure from big picture to details.',
  },
  {
    key: 'code-review',
    name: 'Reviewer',
    icon: '📋',
    content: 'You are a thorough code reviewer. Check for correctness, edge cases, naming clarity, consistency with existing patterns, and unnecessary complexity. Be constructive — suggest specific improvements, not vague criticism.',
  },
  {
    key: 'performance',
    name: 'Performance',
    icon: '⚡',
    content: 'You are a performance engineer. Profile before optimizing. Focus on algorithmic complexity, memory allocation, render cycles, bundle size, and database query efficiency. Measure, don\'t guess. Suggest benchmarks.',
  },
] as const;


// Wave Coordinator shared types
export type WaveStatusType = 'computing' | 'executing' | 'paused' | 'completed' | 'failed';
export type PlanPhaseStatusType = 'pending' | 'executing' | 'awaiting-merge' | 'completed' | 'failed';

export interface WaveDefinitionSummary {
  index: number;
  label: string;
  planPhases: PlanPhaseRefSummary[];
  status: 'pending' | 'executing' | 'completed' | 'failed';
}

export interface PlanPhaseRefSummary {
  planId: string;
  planName: string;
  projectKey: string;
  phaseNumber: number;
  branch?: string;
  prNumber?: number;
  status: PlanPhaseStatusType;
}

export interface ConflictEntrySummary {
  planIdA: string;
  phaseA: number;
  planIdB: string;
  phaseB: number;
  sharedFiles: string[];
}

export interface WaveExecutionSummary {
  id: string;
  userId: string;
  planIds: string[];
  waves: WaveDefinitionSummary[];
  conflictMatrix: ConflictEntrySummary[];
  currentWaveIndex: number;
  status: WaveStatusType;
  startedAt: string;
  completedAt?: string | null;
  pausedAt?: string | null;
  pauseReason?: string | null;
}

// ─── AskUserQuestion payload types ────────────────────────────────────────────

export interface AskUserQuestionOption {
  label: string;
  description: string;
  preview?: string;
}

export interface InitializeParams {
  audioFormat?: string;
  richText?: boolean;
  permissionsManager?: string;
}

export interface InitializeResult {
  status: string;
}

export interface AskUserQuestionData {
  question: string;
  header: string;
  options: AskUserQuestionOption[];
  multiSelect?: boolean;
  annotations?: Record<string, { notes?: string; preview?: string }>;
}

/** WsEvent.AGENT_QUESTION payload shape */
export interface AgentQuestionPayload {
  questions: AskUserQuestionData[];
  sessionId?: string;
  /** DB message id of the streaming assistant message that triggered the question. Drives composerStore streaming → awaiting_human_answer transition. */
  serverMsgId?: string;
}

/** WsEvent.AGENT_QUESTION_ANSWER payload shape (client → server) */
export interface AgentQuestionAnswerPayload {
  /** DB session id whose stream the answer resumes. */
  sessionId: string;
  /** serverMsgId of the AGENT_QUESTION this answers — locates the stored question prose. */
  serverMsgId: string;
  /**
   * The operator's chosen answer, already formatted by the chip UI
   * (e.g. "Model: Opus 4.7", or newline-joined for multi-select). Shown
   * verbatim as the user's chat bubble; also folded into the prose resume.
   */
  answer: string;
}

export interface StreamChunkEnvelope {
  streamId: string;
  seq: number;
  isDone: boolean;
  // Chunk text. The SDK streaming path (claude-stream/gemini/opus) sets `content`;
  // the relay path (voice.gateway.ts onChunk) and plans-execution emit `text`.
  // Both are live on the wire — consumers must read `text || content`.
  content?: string;
  text?: string;
  toolActivity?: any;
  /** Redis-stream event id of THIS event — embedded by the gateway emit helper so the
   *  client can track its replay position (reconnect REPLAY_EVENTS + mid-stream gap recovery). */
  _lastEventId?: string;
}

/** WsEvent.REQUEST_MISSING_CHUNKS payload (Client → Server). `lastEventId` is the Redis
 *  stream id of the last IN-ORDER chunk the client applied — the readAfter() start position,
 *  which sits just before the gap (lost chunks are still in Redis: the gateway appends before
 *  emitting). `expectedSeq` is the first missing chunk seq, used to filter the re-sent chunks. */
export interface RequestMissingChunksPayload {
  sessionId: string;
  expectedSeq: number;
  lastEventId?: string;
}

// Protofac geo allow/block tiers (signup gate)
export * from './country-tiers';

// Model role helpers (embedding-vs-agent) — shared by the harness write-guard + picker filter
export * from './model-roles';

// Class-B restorable view state (uiStateStore schema) — user-ui-preferences-state Phase 1 (#1566)
export * from './ui-state';

/** Agent Runs — engine-agnostic workflow-progress contract (plan: agent-runs-visualization.md) */
export * from './workflow-progress';

/** Usage accumulation correctness — pure dedup/bucketing/pricing core (llm-provider-interop-kimi-k3.md §6.2) */
export * from './usage-accumulation';

/** Drill-in tool timeline — pure derivation over the durable wf:events replay, #M1 truncation (§6.2 step 4) */
export * from './workflow-timeline';

/** WsEvent.TOOL_APPROVAL_REQUEST payload shape (Server → Client) */
export interface ToolApprovalRequestPayload {
  serverMsgId: string;
  toolName: string;
  toolArgs: any;
  reason?: string;
  approvalToken: string;
}

/** WsEvent.TOOL_APPROVAL_RESPONSE payload shape (Client → Server) */
export interface ToolApprovalResponsePayload {
  approvalToken: string;
  approved: boolean;
}
